import type { UserRole } from '@/common/composition/useRoles';
import type { PickByType, ZvdZID } from '@/types';
import type { ScopeType } from '../gebruikersBeheer/views/gebruikers/useGetUserData';

export type SeriesModel = {
  data: number[];
  description: string;
  isTotaal: boolean;
  legendaItemId: number;
  name: string;
  label: string;
  onderdeelGrootte: number;
  onderdeelId: number;
  serieGrootte: number;
  // year: string;
};

export type SerieData = {
  categoriesVerslag: string[];
  seriesVerslag: SeriesModel[];
  categoriesVergelijk: string[];
  seriesVergelijk: SeriesModel[];
};

export type EmployerListItem = {
  id: number;
  parentId: number | null;
  naam: string;
  hasChilds: boolean;
  items?: EmployerListItem[];
  periodA?: number;
};

type DataLijstShared = {
  hasChilds: boolean;
  hierarchischeNaam: string;
  id: number;
  legenda: string;
  naam: string;
  parentId: number;
  toonData: boolean;
  key: number;
  addedOwnEntry: boolean;
};

type DataLijstVerzuimcijfers = {
  gemiddeldePopulatie_A: number;
  gemiddeldePopulatie_ATotal: number;
  gemiddeldePopulatie_B: number;
  gemiddeldePopulatie_BTotal: number;
  gemiddeldePopulatie_Trend: number;
  gemiddeldePopulatie_TrendTotal: number;
  gvD_A: number;
  gvD_ATotal: number;
  gvD_B: number;
  gvD_BTotal: number;
  gvD_Trend: number;
  gvD_TrendTotal: number;
  mF_A: number;
  mF_ATotal: number;
  mF_B: number;
  mF_BTotal: number;
  mF_Trend: number;
  mF_TrendTotal: number;
  vP_A: number;
  vP_ATotal: number;
  vP_B: number;
  vP_BTotal: number;
  vP_Trend: number;
  vP_TrendTotal: number;
};

type DataLijstVerzuimduurklasse = {
  id: ZvdZID;
  parentId: ZvdZID;
  l_A: number;
  l_ATotal: number;
  l_B: number;
  l_BTotal: number;
  l_Trend: number;
  l_TrendTotal: number;
  m_A: number;
  m_ATotal: number;
  m_B: number;
  m_BTotal: number;
  m_Trend: number;
  m_TrendTotal: number;
  s_A: number;
  s_ATotal: number;
  s_B: number;
  s_BTotal: number;
  s_Trend: number;
  s_TrendTotal: number;
  totaal_A: number;
  totaal_ATotal: number;
  totaal_B: number;
  totaal_BTotal: number;
  totaal_Trend: number;
  totaal_TrendTotal: number;
  xL_A: number;
  xL_ATotal: number;
  xL_B: number;
  xL_BTotal: number;
  xL_Trend: number;
  xL_TrendTotal: number;
};

type DataLijstAantalVerzuimen = {
  id: ZvdZID;
  parentId: ZvdZID;
  cat0_A: number;
  cat0_ATotal: number;
  cat0_B: number;
  cat0_BTotal: number;
  cat0_Trend: number;
  cat0_TrendTotal: number;
  cat1_A: number;
  cat1_ATotal: number;
  cat1_B: number;
  cat1_BTotal: number;
  cat1_Trend: number;
  cat1_TrendTotal: number;
  cat2_A: number;
  cat2_ATotal: number;
  cat2_B: number;
  cat2_BTotal: number;
  cat2_Trend: number;
  cat2_TrendTotal: number;
  cat3_A: number;
  cat3_ATotal: number;
  cat3_B: number;
  cat3_BTotal: number;
  cat3_Trend: number;
  cat3_TrendTotal: number;
  catMore_A: number;
  catMore_ATotal: number;
  catMore_B: number;
  catMore_BTotal: number;
  catMore_Trend: number;
  catMore_TrendTotal: number;
};

export type DataLijst = DataLijstShared &
  (
    | DataLijstVerzuimcijfers
    | DataLijstVerzuimduurklasse
    | DataLijstAantalVerzuimen
  );

export type Lijsten = {
  dashboardTypes: Record<string, unknown>[];
  dataLijstAantalVerzuimen: DataLijstAantalVerzuimen[];
  dataLijstAantalVerzuimenKalenderdagen: DataLijstAantalVerzuimen[];
  dataLijstAantalVerzuimenPercentage: DataLijstAantalVerzuimen[];
  dataLijstGemiddeldePopulatie: EmployerListItem[];
  dataLijstVerzuimcijfers: DataLijstVerzuimcijfers[];
  dataLijstVerzuimduurklasse: DataLijstVerzuimduurklasse[];
  dataObjectMeldingsfrequentieFuncties: SerieData;
  dataObjectMeldingsfrequentieGeslacht: SerieData;
  dataObjectMeldingsfrequentieLeeftijdsklasse: SerieData;
  dataObjectMeldingsfrequentieTotaal: SerieData;
  dataObjectMeldingsfrequentieVerzuimduurklasse: SerieData;
  dataObjectVerzuimpercentageFuncties: SerieData;
  dataObjectVerzuimpercentageGeslacht: SerieData;
  dataObjectVerzuimpercentageLeeftijdsklasse: SerieData;
  dataObjectVerzuimpercentageTotaal: SerieData;
  dataObjectVerzuimpercentageVerzuimduurklasse: SerieData;
  meldingsfrequentieLegendaLijst: Record<string, unknown>[];
  rapportBronTypes: Record<string, unknown>[];
  verzuimpercentageLegendaLijst: Record<string, unknown>[];
  widgetViewTypes: Record<string, unknown>[];
};

export type LijstenBySerieData = PickByType<Lijsten, SerieData>;

export type ZichtbaarItem = {
  id: ZvdZID;
  isTotaal: boolean;
  naam?: string;
};
export type DashboardDataSettings = {
  verslagPeriodeAVan: string;
  vergelijkingPeriodeBVan: string;
  zichtbareItems?: ZichtbaarItem[];
};

export type OrganisationHierarchie = {
  aantal?: number;
  afdelingId?: ZvdZID;
  id: ZvdZID;
  organisatieEenheidType: ScopeType;
  text: string;
  title?: string;
  titleWithCount?: string;
  uid: ZvdZID;
  werkgeverId?: ZvdZID;
  rollen?: UserRole[];
  items?: OrganisationHierarchie[];
  uniqueId?: string;
  parentUniqueId?: string;
};

export type Organisation = {
  id: ZvdZID;
  naam: string;
};

export type DashboardData = {
  organisatieHierarchie: OrganisationHierarchie[];
  lijsten: Lijsten;
  settings: DashboardDataSettings;
  widgets: Record<string, unknown>;
};

export type ChartData = {
  labels: string[];
  datasets: SeriesModel[];
};

export enum DashboardType {
  basic = 1,
  extended = 2,
}

export enum BronType {
  employer = 1,
  manager = 2,
}

export type InputModel = {
  organisatieId: number;
  datalijstGemiddeldePopulatieWijzigingen: string[];
  settings: {
    bronTypeId: BronType;
    dashboardTypeId: DashboardType;
    deeltijdFactor: boolean;
    vergelijkingPeriodeBTm: string | null;
    vergelijkingPeriodeBVan: string | null;
    verslagPeriodeATm: string | null;
    verslagPeriodeAVan: string | null;
    zichtbareItems?: ZichtbaarItem[];
  };
};

export enum CategoryVerzuimpercentageData {
  totaal = 'dataObjectVerzuimpercentageTotaal',
  geslacht = 'dataObjectVerzuimpercentageGeslacht',
  leeftijdsklasse = 'dataObjectVerzuimpercentageLeeftijdsklasse',
  verzuimduurklasse = 'dataObjectVerzuimpercentageVerzuimduurklasse',
  functie = 'dataObjectVerzuimpercentageFuncties',
}

export enum CategoryMeldingsfrequentieData {
  totaal = 'dataObjectMeldingsfrequentieTotaal',
  geslacht = 'dataObjectMeldingsfrequentieGeslacht',
  leeftijdsklasse = 'dataObjectMeldingsfrequentieLeeftijdsklasse',
  verzuimduurklasse = 'dataObjectMeldingsfrequentieVerzuimduurklasse',
  functie = 'dataObjectMeldingsfrequentieFuncties',
}

export enum DataLijstenProp {
  verzuimduurklasse = 'dataLijstVerzuimduurklasse',
  aantalVerzuimen = 'dataLijstAantalVerzuimen',
}

export enum Duurklasse {
  S = 'short',
  M = 'medium',
  L = 'long',
  XL = 'extraLong',
}

export enum UitDienstVersion {
  default,
  verzuimend,
}
